import React from 'react';
import { motion } from 'framer-motion';
import { X, Wine, ShoppingCart } from 'lucide-react';
import { Experience } from '../types/Experience';
import { mockWineProducts } from '../data/mockWineProducts';
interface ExperienceDetailViewProps {
  experience: Experience;
  onClose: () => void;
}
export function ExperienceDetailView({
  experience,
  onClose
}: ExperienceDetailViewProps) {
  const isLocked = experience.locked;
  // Get suggested wines for this experience
  const suggestedWines = mockWineProducts.filter(wine => wine.experienceIds.includes(experience.id) && wine.inStock).slice(0, 5); // Limit to 5 suggestions
  return <motion.div className="fixed inset-0 z-50 bg-white overflow-y-auto" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3
  }} data-id="element-58">
      {/* Header with close button */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200" data-id="element-59">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between" data-id="element-60">
          <div className="w-10" data-id="element-61" />
          <h1 className="text-lg font-semibold text-gray-900 text-center flex-1" data-id="element-62">
            {experience.name}
          </h1>
          <button onClick={onClose} className="p-2 -mr-2 text-gray-700 hover:text-gray-900 active:scale-95 transition-all rounded-lg hover:bg-gray-100" aria-label="Chiudi" data-id="element-63">
            <X size={22} strokeWidth={2} data-id="element-64" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-5 py-8" data-id="element-65">
        {/* Hero bubble */}
        <motion.div className="flex items-center justify-center mb-8" initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 0.1,
        type: 'spring',
        stiffness: 200
      }} data-id="element-66">
          <div className="w-32 h-32 rounded-full flex items-center justify-center shadow-lg" style={{
          backgroundColor: isLocked ? '#D7D7D7' : '#6C083B'
        }} data-id="element-67">
            <div className="text-center font-semibold px-4" style={{
            fontSize: '15px',
            lineHeight: '1.3',
            color: isLocked ? '#666666' : '#FFFFFF'
          }} data-id="element-68">
              {experience.name}
            </div>
          </div>
        </motion.div>

        {/* Category badge */}
        {experience.category && <motion.div className="flex justify-center mb-6" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-69">
            <span className="px-4 py-1.5 rounded-full text-xs font-medium" style={{
          backgroundColor: isLocked ? '#F3F4F6' : '#FFF5F7',
          color: isLocked ? '#6B7280' : '#6C083B'
        }} data-id="element-70">
              {experience.category}
            </span>
          </motion.div>}

        {/* Description */}
        <motion.div className="mb-8" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} data-id="element-71">
          <p className="text-gray-700 text-base leading-relaxed text-center" data-id="element-72">
            {experience.description || 'Nessuna descrizione disponibile.'}
          </p>
        </motion.div>

        {/* Wine Description - NEW */}
        {experience.wineDescription && <motion.div className="mb-8 bg-rose-50 rounded-xl p-4" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.28
      }} data-id="element-73">
            <h2 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2" data-id="element-74">
              <Wine size={16} style={{
            color: '#6C083B'
          }} data-id="element-75" />
              Vini consigliati per questa esperienza
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-76">
              {experience.wineDescription}
            </p>
          </motion.div>}

        {/* Wine tags */}
        {experience.wineTags && experience.wineTags.length > 0 && <motion.div className="mb-8" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-77">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 text-center" data-id="element-78">
              Caratteristiche vini
            </h2>
            <div className="flex flex-wrap gap-2 justify-center" data-id="element-79">
              {experience.wineTags.map((tag, index) => <motion.span key={index} className="px-3 py-1.5 rounded-full text-xs font-medium border" style={{
            backgroundColor: isLocked ? '#FFFFFF' : '#FFF5F7',
            borderColor: isLocked ? '#E5E7EB' : '#FDE2E4',
            color: isLocked ? '#6B7280' : '#6C083B'
          }} initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3 + index * 0.05
          }} data-id="element-80">
                  {tag}
                </motion.span>)}
            </div>
          </motion.div>}

        {/* Bottles section OR Suggested Wines */}
        {isLocked ? <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} data-id="element-81">
            <div className="mb-4" data-id="element-82">
              <h2 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-83">
                Vini suggeriti per sbloccare questa esperienza
              </h2>
              <p className="text-xs text-gray-600" data-id="element-84">
                Acquista uno di questi vini per iniziare questa esperienza
              </p>
            </div>

            {suggestedWines.length > 0 ? <div className="space-y-3" data-id="element-85">
                {suggestedWines.map((wine, index) => <motion.div key={wine.id} className="bg-white border border-gray-200 rounded-xl p-4" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4 + index * 0.05
          }} data-id="element-86">
                    <div className="flex items-start gap-3" data-id="element-87">
                      <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0" data-id="element-88">
                        <Wine size={20} style={{
                  color: '#6C083B'
                }} data-id="element-89" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-90">
                        <h3 className="font-semibold text-gray-900 text-sm mb-0.5" data-id="element-91">
                          {wine.name}
                        </h3>
                        <p className="text-xs text-gray-600 mb-1" data-id="element-92">
                          {wine.producer}
                          {wine.year && ` • ${wine.year}`}
                        </p>
                        <div className="flex items-center gap-2 mb-2" data-id="element-93">
                          <span className="text-xs text-gray-500" data-id="element-94">
                            {wine.region}
                          </span>
                          <span className="text-xs font-medium" style={{
                    color: '#6C083B'
                  }} data-id="element-95">
                            {wine.type}
                          </span>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-96">
                          <span className="text-base font-bold text-gray-900" data-id="element-97">
                            €{wine.price.toFixed(2)}
                          </span>
                          <button className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5" style={{
                    backgroundColor: '#6C083B',
                    color: '#FFFFFF'
                  }} data-id="element-98">
                            <ShoppingCart size={14} data-id="element-99" />
                            Acquista
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
              </div> : <div className="bg-gray-50 rounded-2xl p-8 text-center" data-id="element-100">
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4" data-id="element-101">
                  <Wine size={28} className="text-gray-400" data-id="element-102" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2" data-id="element-103">
                  Nessun vino disponibile
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-104">
                  Al momento non ci sono vini suggeriti per questa esperienza
                </p>
              </div>}
          </motion.div> : <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} data-id="element-105">
            <div className="flex items-center justify-between mb-4" data-id="element-106">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-107">
                Bottiglie disponibili
              </h2>
              <span className="px-3 py-1 rounded-full text-xs font-bold" style={{
            backgroundColor: '#6C083B',
            color: '#FFFFFF'
          }} data-id="element-108">
                {experience.bottleCount}
              </span>
            </div>

            {experience.bottles && experience.bottles.length > 0 ? <div className="space-y-3" data-id="element-109">
                {experience.bottles.map((bottle, index) => <motion.div key={bottle.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4 + index * 0.05
          }} data-id="element-110">
                    <div className="flex items-start gap-3" data-id="element-111">
                      <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0" data-id="element-112">
                        <Wine size={20} style={{
                  color: '#6C083B'
                }} data-id="element-113" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-114">
                        <h3 className="font-semibold text-gray-900 text-sm mb-0.5" data-id="element-115">
                          {bottle.name}
                        </h3>
                        <p className="text-xs text-gray-600" data-id="element-116">
                          {bottle.producer}
                          {bottle.year && ` • ${bottle.year}`}
                        </p>
                        {bottle.region && <p className="text-xs text-gray-500 mt-1" data-id="element-117">
                            {bottle.region}
                          </p>}
                      </div>
                    </div>
                  </motion.div>)}
              </div> : <div className="bg-gray-50 rounded-xl p-6 text-center" data-id="element-118">
                <p className="text-sm text-gray-600" data-id="element-119">
                  Hai {experience.bottleCount} bottigl
                  {experience.bottleCount === 1 ? 'a' : 'e'} compatibil
                  {experience.bottleCount === 1 ? 'e' : 'i'} con questa
                  esperienza
                </p>
              </div>}
          </motion.div>}
      </div>
    </motion.div>;
}